/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-object-type */
// 2) Third-party library imports
import {
    isEqual,
    keyBy,
    merge,
    uniqWith,
    values,
    get,
    concat,
} from 'lodash-es';
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';

// 3a) Local imports � Constants
import { REMITTANCE_ADVICES_PAGE, DETAILS_TAB } from '../../config/tableAndPageConstants';
import {
    remittanceAdvicesSortByOptions,
    remittanceAdviceChangesTableFilterOptions,
    remittanceAdviceStateFilterOptions,
} from '../../constants/remittanceAdvicesSortAndFilter';
import { conversationTableFilterOptions } from '../../constants/common';
import { invoicesSortByOptions, invoicesStateFilterOptions } from '../../constants/invoicesSortAndFilters';
import {
    creditsSortByOptions,
    creditsStateFilterOptions,
    rebatesSortByOptions,
    rebatesStateFilterOptions,
} from '../../constants/creditsSortAndFilters';
import { customersSortByOptionsMap } from '../../constants/customersSortAndFilters';
import { salesOrdersSortByOptions, salesOrdersStateFilterOptions } from '../../constants/salesOrdersSortAndFilters';

// 3c) Local imports � Project-specific modules / types
import {
    GetRemittanceAdviceChangesRequestPayload,
    GetRemittanceAdvicesRequestPayload,
    RAProcessingConfiguration,
    RemittanceAdvice,
    RemittanceAdviceAddCommentRequestPayload,
    RemittanceAdviceCredits,
    RemittanceAdviceInvoice,
    RemittanceAdviceProcessorReport,
    RemittanceAdviceProcessorReportState,
    RemittanceAdviceRebates,
    RemittanceAdvicesState,
} from './types';
import { PageData, Conversation, GetConversationRequestPayload } from '../common/types';
import { Customer, GetCustomersRequestPayload } from '../customers/types';
import { GetInvoicesRequestPayload, Invoice } from '../invoices/types';
import { Credit, GetCreditsRequestPayload } from '../credits/types';
import { GetPaymentsRequestPayload, Payment } from '../payments/types';
import { GetSalesOrdersRequestPayload, SalesOrder } from '../salesOrders/types';
import type { RootState } from '../storeTypes';

// 3d) Local imports � Utility functions / interfaces
import { DynamicObject, ResponseModalObject } from '../../utils/commonInterfaces';

const actionPrefix = 'remittanceAdvice';

// Type-safe initialState!
export const initialState: RemittanceAdvicesState = {
    loading: false,
    errorMessages: [],
    activeData: {
        customers: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CUSTOMER_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                Customer: '',
                CustomerCode: ''
            },
            customerState: '',
            sortBy: customersSortByOptionsMap.customerCode.value,
            sortAscending: true,
        },
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            invoiceState: get(invoicesStateFilterOptions, `${1}.value`),
            sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        salesOrders: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.SALES_ORDER_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                SalesOrderNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            salesOrderState: get(salesOrdersStateFilterOptions, `${1}.value`),
            sortBy: salesOrdersSortByOptions[0].value,
            sortAscending: true,
        },
        credits: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditState: get(creditsStateFilterOptions, `${1}.value`),
            sortBy: creditsSortByOptions[0].value,
            sortAscending: true,
        },
        rebates: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditState: get(rebatesStateFilterOptions, `${1}.value`),
            sortBy: rebatesSortByOptions[0].value,
            sortAscending: true,
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        remittanceAdviceChanges: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            remittanceAdviceChangesTableFilter: get(
                remittanceAdviceChangesTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        Allocation: {
            AllocationErrors: [],
            AllocationItems: [],
        },
        record: undefined,
        loading: false,
        selectedId: null,
        errorMessages: [],
        
        payments: []
    },
    data: [],
    pageData: {
        pageSize: REMITTANCE_ADVICES_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {},
    tableFilter: undefined,
    SortField: remittanceAdvicesSortByOptions[1].value, //1 for received date
    sortAscending: false, // Descending order by default
    raProcessingConfiguration: {
        saveLoading: false,
        loading: false,
        errorMessages: [],
        data: undefined
    },
    ActiveState: get(remittanceAdviceStateFilterOptions, `${0}.value`),
    remittanceAdviceProcessorReport: {
        loading: false,
        errorMessages: [],
        data: undefined
    },
};

const remittanceAdviceSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getRemittanceAdvicesRequestAction: (
            state,
            _action: PayloadAction<GetRemittanceAdvicesRequestPayload>
        ) => {
            state.loading = true;
        },
        getRemittanceAdvicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: DynamicObject[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            const { data, pageData, mergeData } = action.payload;
            const remittanceAdviceData = data as RemittanceAdvice[];

            if (mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(remittanceAdviceData, 'Id')
                    )
                );
            } else if (pageData.currentPage === 0) {
                state.data = remittanceAdviceData;
            } else {
                state.data = uniqWith([...state.data, ...remittanceAdviceData], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        updateRemittanceAdvicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                SortField: GetRemittanceAdvicesRequestPayload['SortField'];
                sortAscending: boolean;
                ActiveState: string;
            }>
        ) => {
            state.SortField = action.payload.SortField;
            state.sortAscending = action.payload.sortAscending;
            state.ActiveState = action.payload.ActiveState;
        },
        updateRemittanceAdvicesFiltersAction: (
            state,
            action: PayloadAction<GetRemittanceAdvicesRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        updateRemittanceAdviceTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        setRemittanceAdviceSelectedIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    remittanceAdviceId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                remittanceAdviceId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    remittanceAdviceId,
                    callback
                }
            })
        },
        setRemittanceAdviceSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getRemittanceAdviceDetails: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = true;
        },
        getRemittanceAdviceDetailsSuccess: (
            state,
            action: PayloadAction<{
                record: undefined,
                payment: {}
            }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.payments = [...state.activeData.payments, action.payload.payment as Payment]
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getRemittanceAdviceDetailsError: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = undefined;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        getRemittanceAdviceSummary: {
            reducer: (
                state,
                _action: PayloadAction<{
                    remittanceAdviceId: string;
                    companyId: string | undefined;
                }>
            ) => {
                state.activeData.record = undefined;
                state.activeData.loading = true;
            },
            prepare: (
                remittanceAdviceId: string,
                companyId: string | undefined
            ) => ({
                payload: {
                    remittanceAdviceId,
                    companyId
                }
            })
        },
        getRemittanceAdviceSummarySuccess: (
            state,
            action: PayloadAction<{
                record: {},
                payment: {}
            }>
        ) => {
            state.activeData.record = action.payload.record as RemittanceAdvice;
            state.activeData.payments = [...state.activeData.payments, action.payload.payment as Payment];
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
        },
        getRemittanceAdviceSummaryError: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = undefined;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearRemittanceAdviceDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getRemittanceAdviceCustomersRequestAction: (
            state,
            _action: PayloadAction<GetRemittanceAdvicesRequestPayload>
        ) => {
            state.activeData.customers.loading = true;
        },
        getRemittanceAdviceCustomersSuccessAction: (
            state,
            action: PayloadAction<{
                data: Customer[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.customers.data = action.payload.data;
            } else {
                state.activeData.customers.data = uniqWith(
                    [...state.activeData.customers.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.customers.loading = false;
            state.activeData.customers.pageData = action.payload.pageData;
            state.activeData.customers.errorMessages = initialState.activeData.customers.errorMessages;
        },
        getRemittanceAdviceCustomersErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.customers.loading = false;
            state.activeData.customers.data = initialState.activeData.customers.data;
            state.activeData.customers.errorMessages = action.payload;
        },
        updateRemittanceAdviceCustomersFiltersAction: (
            state,
            action: PayloadAction<GetCustomersRequestPayload['filters']>
        ) => {
            state.activeData.customers.filters = {
                Customer: action.payload.Customer,
                CustomerCode: action.payload.Customer
            };
        },
        updateRemittanceAdviceCustomersSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCustomersRequestPayload['sortBy'];
                sortAscending: boolean;
                customerState: string;
            }>
        ) => {
            state.activeData.customers.sortBy = action.payload.sortBy;
            state.activeData.customers.sortAscending = action.payload.sortAscending;
            state.activeData.customers.customerState = action.payload.customerState;
        },
        updateRemittanceAdviceCustomersTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.customers.customerState = action.payload;
        },
        getRemittanceAdviceInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetInvoicesRequestPayload>
        ) => {
            state.activeData.invoices.loading = true;
        },
        getRemittanceAdviceInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoices.data = action.payload.data;
            } else {
                state.activeData.invoices.data = uniqWith(
                    [...state.activeData.invoices.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.invoices.loading = false;
            state.activeData.invoices.pageData = action.payload.pageData;
            state.activeData.invoices.errorMessages = initialState.activeData.invoices.errorMessages;
        },
        getConfirmRemittanceAdviceInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: RemittanceAdviceInvoice[];
                pageData: PageData;
            }>
        ) => {
            state.activeData.invoices.loading = false;
            state.activeData.invoices.data = action.payload.data as unknown as Invoice[];
            state.activeData.invoices.pageData = action.payload.pageData;
            state.activeData.invoices.errorMessages = [];
        },
        getConfirmRemittanceAdviceCreditsSuccessAction: (
            state,
            action: PayloadAction<{
                data: RemittanceAdviceCredits[];
                pageData: PageData;
            }>
        ) => {
            state.activeData.credits.loading = false;
            state.activeData.credits.data = action.payload.data as unknown as Credit[];
            state.activeData.credits.pageData = action.payload.pageData;
            state.activeData.credits.errorMessages = [];
        },
        getConfirmRemittanceAdviceRebatesSuccessAction: (
            state,
            action: PayloadAction<{
                data: RemittanceAdviceRebates[];
                pageData: PageData;
            }>
        ) => {
            state.activeData.rebates.loading = false;
            state.activeData.rebates.data = action.payload.data as unknown as Credit[];
            state.activeData.rebates.pageData = action.payload.pageData;
            state.activeData.rebates.errorMessages = [];
        },
        getRemittanceAdviceInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.invoices.loading = false;
            state.activeData.invoices.data = [];
            state.activeData.invoices.errorMessages = action.payload;
        },
        updateRemittanceAdviceInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetInvoicesRequestPayload['filters']>
        ) => {
            state.activeData.invoices.filters = action.payload;
        },
        updateRemittanceAdviceInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.activeData.invoices.sortBy = action.payload.sortBy;
            state.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        updateRemittanceAdviceInvoicesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.invoices.invoiceState = action.payload;
        },
        getRemittanceAdviceSalesOrdersRequestAction: (
            state,
            action: PayloadAction<GetSalesOrdersRequestPayload>
        ) => {
            state.activeData.salesOrders.loading = true;
        },
        getRemittanceAdviceSalesOrdersSuccessAction: (
            state,
            action: PayloadAction<{
                data: SalesOrder[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;
            let newDataState: SalesOrder[];

            if (pageData.currentPage === 0) {
                newDataState = data;
            } else {
                newDataState = uniqWith(concat(state.activeData.salesOrders.data, data), isEqual);
            }

            state.activeData.salesOrders.loading = false;
            state.activeData.salesOrders.data = newDataState;
            state.activeData.salesOrders.pageData = pageData;
            state.activeData.salesOrders.errorMessages = initialState.activeData.salesOrders.errorMessages;
        },
        getRemittanceAdviceSalesOrdersErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.salesOrders.loading = false;
            state.activeData.salesOrders.data = initialState.activeData.salesOrders.data;
            state.activeData.salesOrders.errorMessages = action.payload;
        },
        updateRemittanceAdviceSalesOrdersFiltersAction: (
            state,
            action: PayloadAction<GetSalesOrdersRequestPayload['filters']>
        ) => {
            state.activeData.salesOrders.filters = action.payload;
        },
        updateRemittanceAdviceSalesOrdersSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetSalesOrdersRequestPayload['sortBy'];
                sortAscending: boolean;
                salesOrderState: string;
            }>
        ) => {
            state.activeData.salesOrders.sortBy = action.payload.sortBy;
            state.activeData.salesOrders.sortAscending = action.payload.sortAscending;
            state.activeData.salesOrders.salesOrderState = action.payload.salesOrderState;
        },
        updateRemittanceAdviceSalesOrdersTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.salesOrders.salesOrderState = action.payload;
        },
        getRemittanceAdviceCreditsRequestAction: (
            state,
            _action: PayloadAction<GetCreditsRequestPayload>
        ) => {
            state.activeData.credits.loading = true;
        },
        getRemittanceAdviceCreditsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Credit[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.credits.data = action.payload.data;
            } else {
                state.activeData.credits.data = uniqWith(
                    [...state.activeData.credits.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.credits.loading = false;
            state.activeData.credits.pageData = action.payload.pageData;
            state.activeData.credits.errorMessages = [];
        },
        getRemittanceAdviceCreditsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.credits.loading = false;
            state.activeData.credits.data = [];
            state.activeData.credits.errorMessages = action.payload;
        },
        updateRemittanceAdviceCreditsFiltersAction: (
            state,
            action: PayloadAction<GetCreditsRequestPayload['filters']>
        ) => {
            state.activeData.credits.filters = action.payload;
        },
        updateRemittanceAdviceCreditsSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCreditsRequestPayload['sortBy'];
                sortAscending: boolean;
                creditState: string;
            }>
        ) => {
            state.activeData.credits.sortBy = action.payload.sortBy;
            state.activeData.credits.sortAscending = action.payload.sortAscending;
            state.activeData.credits.creditState = action.payload.creditState;
        },
        updateRemittanceAdviceCreditsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.credits.creditState = action.payload;
        },
        getRemittanceAdviceRebatesRequestAction: (
            state,
            _action: PayloadAction<GetCreditsRequestPayload>
        ) => {
            state.activeData.rebates.loading = true;
        },
        getRemittanceAdviceRebatesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Credit[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.rebates.data = action.payload.data;
            } else {
                state.activeData.rebates.data = uniqWith(
                    [...state.activeData.rebates.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.rebates.loading = false;
            state.activeData.rebates.pageData = action.payload.pageData;
            state.activeData.rebates.errorMessages = [];
        },
        getRemittanceAdviceRebatesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.rebates.loading = false;
            state.activeData.rebates.data = [];
            state.activeData.rebates.errorMessages = action.payload;
        },
        updateRemittanceAdviceRebatesFiltersAction: (
            state,
            action: PayloadAction<GetCreditsRequestPayload['filters']>
        ) => {
            state.activeData.rebates.filters = action.payload;
        },
        updateRemittanceAdviceRebatesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCreditsRequestPayload['sortBy'];
                sortAscending: boolean;
                creditState: string;
            }>
        ) => {
            state.activeData.rebates.sortBy = action.payload.sortBy;
            state.activeData.rebates.sortAscending = action.payload.sortAscending;
            state.activeData.rebates.creditState = action.payload.creditState;
        },
        updateRemittanceAdviceRebatesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.rebates.creditState = action.payload;
        },
        getRemittanceAdviceConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getRemittanceAdviceConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            } else {
                state.activeData.conversation.data = uniqWith(
                    [...state.activeData.conversation.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = [];
        },
        getRemittanceAdviceConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = [];
            state.activeData.conversation.errorMessages = action.payload;
        },
        updateRemittanceAdviceConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            state.activeData.conversation.filters = action.payload;
        },
        updateRemittanceAdviceConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateRemittanceAdviceConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        getRemittanceAdviceChangesRequestAction: (
            state,
            _action: PayloadAction<GetRemittanceAdviceChangesRequestPayload>
        ) => {
            state.activeData.remittanceAdviceChanges.loading = true;
        },
        getRemittanceAdviceChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.remittanceAdviceChanges.data = action.payload.data as unknown as RemittanceAdvice[];
            } else {
                state.activeData.remittanceAdviceChanges.data = uniqWith(
                    [...state.activeData.remittanceAdviceChanges.data, ...action.payload.data as unknown as RemittanceAdvice[]],
                    isEqual
                );
            }

            // Update other state properties directly (Immer takes care of immutability)
            state.activeData.remittanceAdviceChanges.loading = false;
            state.activeData.remittanceAdviceChanges.pageData = action.payload.pageData;
            state.activeData.remittanceAdviceChanges.errorMessages = [];
        },
        getRemittanceAdviceChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.remittanceAdviceChanges.loading = false;
            state.activeData.remittanceAdviceChanges.data = [];
            state.activeData.remittanceAdviceChanges.errorMessages = action.payload;
        },
        updateRemittanceAdviceChangesFiltersAction: (
            state,
            action: PayloadAction<GetRemittanceAdviceChangesRequestPayload['filters']>
        ) => {
            state.activeData.remittanceAdviceChanges.filters = action.payload;
        },
        updateRemittanceAdviceChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.remittanceAdviceChanges.remittanceAdviceChangesTableFilter = action.payload;
        },
        updateRemittanceAdviceChangesSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetRemittanceAdviceChangesRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.remittanceAdviceChanges.sortBy = action.payload.sortBy;
            state.activeData.remittanceAdviceChanges.sortAscending = action.payload.sortAscending;
        },
        reviewRemittanceAdviceSuccess: (
            state,
            action: PayloadAction<{
                AllocationErrors: DynamicObject[];
                AllocationItems: DynamicObject[];
            }>
        ) => {
            state.activeData.Allocation.AllocationErrors = action.payload.AllocationErrors;
            state.activeData.Allocation.AllocationItems = action.payload.AllocationItems;
        },
        getRAProcessingConfigurationRequestAction: (
            state
        ) => {
            state.raProcessingConfiguration.loading = true;
        },
        getRAProcessingConfigurationSuccessAction: (
            state,
            action: PayloadAction<RAProcessingConfiguration>
        ) => {
            state.raProcessingConfiguration.loading = false;
            state.raProcessingConfiguration.data = action.payload;
        },
        getRAProcessingConfigurationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.raProcessingConfiguration.loading = false;
            state.raProcessingConfiguration.errorMessages = action.payload;
        },
        saveRAProcessingConfigurationRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    payload: RAProcessingConfiguration;
                    callback?: (response: ResponseModalObject) => void;
                }>
            ) => {
                state.raProcessingConfiguration.saveLoading = true;
                state.raProcessingConfiguration.data = action.payload.payload;
            },
            prepare: (
                payload: RAProcessingConfiguration,
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: {
                    payload,
                    callback
                }
            })
        },
        saveRAProcessingConfigurationSuccessAction: (state) => {
            state.raProcessingConfiguration.saveLoading = false;
        },
        saveRAProcessingConfigurationErrorAction: (state) => {
            state.raProcessingConfiguration.saveLoading = false;
        },
        saveRARebateMappingConfigurationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: any;
                    callback?: (response: ResponseModalObject) => void;
                }>
            ) => {
                state.loading = true;
            },
            prepare: (
                payload: any,
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: { payload, callback }
            })
        },
        saveRARebateMappingConfigurationResponseAction: (state) => {
            state.loading = false;
        },
        getRemittanceAdviceProcessorReportRequest: (
            state,
            _action: PayloadAction<{
                remittanceAdviceId: string;
                companyId?: string | undefined
            }>
        ) => {
            state.remittanceAdviceProcessorReport.loading = true;
        },
        getRemittanceAdviceProcessorReportSuccessAction: (
            state,
            action: PayloadAction<RemittanceAdviceProcessorReport[]>
        ) => {
            state.remittanceAdviceProcessorReport.loading = false;
            state.remittanceAdviceProcessorReport.data = action.payload;
        },
        getRemittanceAdviceProcessorReportErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.remittanceAdviceProcessorReport.loading = false;
            state.remittanceAdviceProcessorReport.errorMessages = action.payload;
        },
    }
});

const { actions, reducer } = remittanceAdviceSlice;

// Actions
export const {
    getRemittanceAdvicesRequestAction,
    getRemittanceAdvicesSuccessAction,
    updateRemittanceAdvicesSortByAndStateAction,
    updateRemittanceAdvicesFiltersAction,
    updateRemittanceAdviceTableFilterAction,
    setRemittanceAdviceSelectedIdRequestAction,
    setRemittanceAdviceSelectedIdSuccessAction,
    getRemittanceAdviceDetails,
    getRemittanceAdviceDetailsSuccess,
    getRemittanceAdviceDetailsError,
    getRemittanceAdviceSummary,
    getRemittanceAdviceSummarySuccess,
    getRemittanceAdviceSummaryError,
    clearRemittanceAdviceDataAction,
    getRemittanceAdviceCustomersRequestAction,
    getRemittanceAdviceCustomersSuccessAction,
    getRemittanceAdviceCustomersErrorAction,
    updateRemittanceAdviceCustomersFiltersAction,
    updateRemittanceAdviceCustomersSortByAndStateAction,
    updateRemittanceAdviceCustomersTableFilterAction,
    getRemittanceAdviceInvoicesRequestAction,
    getRemittanceAdviceInvoicesSuccessAction,
    getConfirmRemittanceAdviceInvoicesSuccessAction,
    getConfirmRemittanceAdviceCreditsSuccessAction,
    getConfirmRemittanceAdviceRebatesSuccessAction,
    getRemittanceAdviceInvoicesErrorAction,
    updateRemittanceAdviceInvoicesFiltersAction,
    updateRemittanceAdviceInvoicesSortByAndStateAction,
    updateRemittanceAdviceInvoicesTableFilterAction,
    getRemittanceAdviceSalesOrdersRequestAction,
    getRemittanceAdviceSalesOrdersSuccessAction,
    getRemittanceAdviceSalesOrdersErrorAction,
    updateRemittanceAdviceSalesOrdersFiltersAction,
    updateRemittanceAdviceSalesOrdersSortByAndStateAction,
    updateRemittanceAdviceSalesOrdersTableFilterAction,
    getRemittanceAdviceCreditsRequestAction,
    getRemittanceAdviceCreditsSuccessAction,
    getRemittanceAdviceCreditsErrorAction,
    updateRemittanceAdviceCreditsFiltersAction,
    updateRemittanceAdviceCreditsSortByAndStateAction,
    updateRemittanceAdviceCreditsTableFilterAction,
    getRemittanceAdviceRebatesRequestAction,
    getRemittanceAdviceRebatesSuccessAction,
    getRemittanceAdviceRebatesErrorAction,
    updateRemittanceAdviceRebatesFiltersAction,
    updateRemittanceAdviceRebatesSortByAndStateAction,
    updateRemittanceAdviceRebatesTableFilterAction,
    getRemittanceAdviceConversationRequestAction,
    getRemittanceAdviceConversationSuccessAction,
    getRemittanceAdviceConversationErrorAction,
    updateRemittanceAdviceConversationFiltersAction,
    updateRemittanceAdviceConversationTableFilterAction,
    updateRemittanceAdviceConversationSortByAction,
    getRemittanceAdviceChangesRequestAction,
    getRemittanceAdviceChangesSuccessAction,
    getRemittanceAdviceChangesErrorAction,
    updateRemittanceAdviceChangesFiltersAction,
    updateRemittanceAdviceChangesTableFilterAction,
    updateRemittanceAdviceChangesSortByAction,
    reviewRemittanceAdviceSuccess,
    getRAProcessingConfigurationRequestAction,
    getRAProcessingConfigurationSuccessAction,
    getRAProcessingConfigurationErrorAction,
    saveRAProcessingConfigurationRequestAction,
    saveRAProcessingConfigurationSuccessAction,
    saveRAProcessingConfigurationErrorAction,
    saveRARebateMappingConfigurationRequestAction,
    saveRARebateMappingConfigurationResponseAction,
    getRemittanceAdviceProcessorReportRequest,
    getRemittanceAdviceProcessorReportSuccessAction,
    getRemittanceAdviceProcessorReportErrorAction
} = actions;

export const remittanceAdviceAddCommentRequestAction = createAction(
    `${actionPrefix}/remittanceAdviceAddCommentRequestAction`,
    function prepare(
        payload: RemittanceAdviceAddCommentRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const confirmRemittanceAdviceRequest = createAction(
    `${actionPrefix}/confirmRemittanceAdviceRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const confirmRemittanceAdviceDisregardTasksRequest = createAction(
    `${actionPrefix}/confirmRemittanceAdviceDisregardTasksRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const organisationConfirmRemittanceAdviceDisregardTasksRequest = createAction(
    `${actionPrefix}/organisationConfirmRemittanceAdviceDisregardTasksRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const paymentAddCommentRequestAction = createAction(
    `${actionPrefix}/paymentAddCommentRequestAction`,
    function prepare(
        payload: RemittanceAdviceAddCommentRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const getRemittanceAdvicePaymentsRequestAction = createAction(
    `${actionPrefix}/getRemittanceAdvicePaymentsRequestAction`,
    function prepare(
        payload: GetCreditsRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const getRemittanceAdvicePaymentsSuccessAction = createAction(
    `${actionPrefix}/getRemittanceAdvicePaymentsSuccessAction`,
    function prepare(payload: {
        data: Payment[];
        pageData: PageData;
    }) {
        return {
            payload
        };
    }
);

export const getRemittanceAdvicePaymentsErrorAction = createAction(
    `${actionPrefix}/getRemittanceAdvicePaymentsErrorAction`,
    function prepare(payload: string[]) {
        return {
            payload
        };
    }
);

export const updateRemittanceAdvicePaymentsFiltersAction = createAction(
    `${actionPrefix}/updateRemittanceAdvicePaymentsFiltersAction`,
    function prepare(filters: GetPaymentsRequestPayload['filters']) {
        return {
            payload: {
                filters
            }
        };
    }
);

export const updateRemittanceAdvicePaymentsSortByAction = createAction(
    `${actionPrefix}/updateRemittanceAdvicePaymentsSortByAction`,
    function prepare(tableSort: {
        sortBy: GetPaymentsRequestPayload['sortBy'];
        sortAscending: boolean;
    }) {
        return {
            payload: {
                tableSort
            }
        };
    }
);

export const updateRemittanceAdvicePaymentsTableFilterAction = createAction(
    `${actionPrefix}/updateRemittanceAdvicePaymentsTableFilterAction`,
    function prepare(paymentState: string) {
        return {
            payload: {
                paymentState
            }
        };
    }
);

export const getRACustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getRACustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { companyId, callback },
        };
    }
);

export const getRACustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getRACustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        };
    }
);

export const reviewRemittanceAdviceRequest = createAction(
    `${actionPrefix}/reviewRemittanceAdviceRequest`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const reviewOrganisationRemittanceAdviceRequest = createAction(
    `${actionPrefix}/reviewOrganisationRemittanceAdviceRequest`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const getRARebateMappingsRequestAction = createAction(
    `${actionPrefix}/getRARebateMappingsRequestAction`,
    function prepare(callback: (response: DynamicObject) => void) {
        return {
            payload: {
                callback
            }
        };
    }
);

export const saveRemittanceAdvice = createAction(
    `${actionPrefix}/saveRemittanceAdvice`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const saveRemittanceAdviceInvoice = createAction(
    `${actionPrefix}/saveRemittanceAdviceInvoice`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const saveRemittanceAdviceCredit = createAction(
    `${actionPrefix}/saveRemittanceAdviceCredit`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const saveRemittanceAdviceRebate = createAction(
    `${actionPrefix}/saveRemittanceAdviceRebate`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const createAdjustmentsForInvoicesRequest = createAction(
    `${actionPrefix}/createAdjustmentsForInvoicesRequest`,
    function prepare(payload: {}) {
        return {
            payload,
        };
    }
);

export const getRAImportStatusRequestAction = createAction(
    `${actionPrefix}/getRAImportStatusRequestAction`,
    function prepare(
        RemittanceAdviceId: string,
        CompanyId: string,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                RemittanceAdviceId,
                CompanyId,
                callback
            }
        }
    }
);

// Selectors
export const getRemittanceAdvicesState = (state: RootState) => state.remittanceAdvices;
export const getRaProcessingConfiguration = createSelector(
    getRemittanceAdvicesState,
    (remittanceAdvices) => remittanceAdvices.raProcessingConfiguration
);
export const getRemittanceAdviceActiveData = createSelector(
    getRemittanceAdvicesState,
    (remittanceAdvices) => remittanceAdvices.activeData
);
export const getRemittanceAdviceActiveDataInvoices = createSelector(
    getRemittanceAdviceActiveData,
    (remittanceAdvicesActiveData) => remittanceAdvicesActiveData.invoices
);
export const getRemittanceAdviceActiveDataCredits = createSelector(
    getRemittanceAdviceActiveData,
    (remittanceAdvicesActiveData) => remittanceAdvicesActiveData.credits
);
export const getRemittanceAdviceActiveDataRebates = createSelector(
    getRemittanceAdviceActiveData,
    (remittanceAdvicesActiveData) => remittanceAdvicesActiveData.rebates
);
export const getRemittanceAdviceActiveDataAllocation = createSelector(
    getRemittanceAdviceActiveData,
    (remittanceAdvicesActiveData) => remittanceAdvicesActiveData.Allocation
);
export const getRemittanceAdviceActiveDataAllocationItems = createSelector(
    getRemittanceAdviceActiveDataAllocation,
    (allocation) => allocation.AllocationItems
);
export const getRemittanceAdviceActiveDataAllocationErrors = createSelector(
    getRemittanceAdviceActiveDataAllocation,
    (allocation) => allocation.AllocationErrors
);
export const getRemittanceAdviceSelectedId = createSelector(
    getRemittanceAdviceActiveData,
    (remittanceAdvicesActiveData) => remittanceAdvicesActiveData.selectedId
);
export const getRemittanceAdvicesPageData = createSelector(
    getRemittanceAdvicesState,
    (remittanceAdvices) => remittanceAdvices.pageData
);
export const selectRemittanceAdviceData = createSelector(
    getRemittanceAdvicesState,
    (remittanceAdvices) => remittanceAdvices.activeData
);

// Reducer
export default reducer;